'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, ChevronRight, Shield } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Compare Plans', href: '/policy-listing-filters' },
  { label: 'Side-by-Side', href: '/policy-comparison' },
  { label: 'How It Works', href: '/#how-it-works' },
];

export default function Topbar() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isActive = (href: string) => {
    if (href.startsWith('/#')) return false;
    return pathname === href || pathname.startsWith(href + '/');
  };

  return (
    <>
      <header
        className={`sticky-top w-full transition-all duration-200 ${
          scrolled
            ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-card/80 backdrop-blur-sm border-b border-border/60'
        }`}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0 group">
              <AppLogo size={36} />
              <span className="font-extrabold text-xl tracking-tight text-foreground group-hover:text-primary transition-colors duration-150">
                MediCompare
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  href={link.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                    isActive(link.href)
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5 text-accent" />
                <span>Independent Platform</span>
              </div>
              <Link
                href="/policy-listing-filters"
                className="btn-primary text-sm px-4 py-2"
              >
                Compare Plans
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden animate-fade-in">
          <div
            className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-0 left-0 right-0 bg-card border-b border-border shadow-xl animate-slide-up">
            <div className="flex items-center justify-between px-4 h-16 border-b border-border">
              <Link href="/" className="flex items-center gap-2.5">
                <AppLogo size={32} />
                <span className="font-extrabold text-lg tracking-tight">MediCompare</span>
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg text-muted-foreground hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  href={link.href}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 ${
                    isActive(link.href)
                      ? 'bg-primary/10 text-primary' :'text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-border">
                <Link href="/policy-listing-filters" className="btn-primary w-full justify-center">
                  Compare Plans Free
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="w-3.5 h-3.5 text-accent" />
                <span>Independent Comparison Platform</span>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}